import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, Globe } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { HorizonLogo } from './HorizonLogo';

const langs = [
  { code: 'en', label: 'EN', dir: 'ltr' },
  { code: 'fr', label: 'FR', dir: 'ltr' },
  { code: 'es', label: 'ES', dir: 'ltr' },
  { code: 'ar', label: 'العربية', dir: 'rtl' },
];

const Header = () => {
  const { t, i18n } = useTranslation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);

  const currentLang = langs.find((l) => l.code === i18n.language) || langs[0];

  const changeLang = (code) => {
    i18n.changeLanguage(code);
    const lang = langs.find((l) => l.code === code);
    document.documentElement.dir = lang?.dir || 'ltr';
    setLangOpen(false);
  };

  useEffect(() => {
    document.documentElement.dir = currentLang.dir;
  }, [currentLang.dir]);

  const navLinks = [
    { href: '#services', label: t('nav.services') },
    { href: '#about', label: t('nav.about') },
    { href: '#pricing', label: t('nav.pricing') },
    { href: '/templates', label: 'Templates', isRoute: true },
    { href: '#contact', label: t('nav.contact') },
  ];

  const scrollToSection = (href) => {
    document.querySelector(href)?.scrollIntoView({ behavior: 'smooth' });
    setIsMobileMenuOpen(false);
  };

  return (
    <>
      <header data-testid="main-header" className="relative z-50 bg-[#FAF8F5] py-3 sm:py-4">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <nav className="flex items-center justify-between">
            <Link to="/" data-testid="header-logo" aria-label="Horizon — Home" className="flex items-center group">
              <HorizonLogo scale={0.45} className="block transition-opacity duration-300 group-hover:opacity-80" />
            </Link>

            <div className="hidden lg:flex items-center gap-10">
              {navLinks.map((link) => (
                link.isRoute ? (
                  <Link key={link.href} to={link.href}
                    data-testid={`nav-templates`}
                    className="relative text-sm font-medium tracking-wide text-[#1A1816]/70 hover:text-[#1A1816] transition-colors group">
                    {link.label}
                    <span className="absolute -bottom-1 left-0 w-0 h-[1px] bg-[#C9A96E] transition-all duration-300 group-hover:w-full" />
                  </Link>
                ) : (
                  <button key={link.href} onClick={() => scrollToSection(link.href)}
                    data-testid={`nav-${link.href.replace('#', '')}`}
                    className="relative text-sm font-medium tracking-wide text-[#1A1816]/70 hover:text-[#1A1816] transition-colors group">
                    {link.label}
                    <span className="absolute -bottom-1 left-0 w-0 h-[1px] bg-[#C9A96E] transition-all duration-300 group-hover:w-full" />
                  </button>
                )
              ))}
            </div>

            <div className="hidden lg:flex items-center gap-4">
              {/* Language switcher */}
              <div className="relative">
                <button onClick={() => setLangOpen(!langOpen)} data-testid="lang-switcher"
                  className="flex items-center gap-1.5 text-sm text-[#8A857D] hover:text-[#1A1816] transition-colors">
                  <Globe className="w-4 h-4" />
                  <span className="text-xs font-semibold tracking-wide">{currentLang.label}</span>
                </button>
                <AnimatePresence>
                  {langOpen && (
                    <motion.div
                      initial={{ opacity: 0, y: -5 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -5 }}
                      className="absolute top-8 right-0 bg-[#FAF8F5] border border-[#1A1816]/10 py-2 min-w-[100px] z-50">
                      {langs.map((l) => (
                        <button key={l.code} onClick={() => changeLang(l.code)}
                          data-testid={`lang-${l.code}`}
                          className={`block w-full text-left px-4 py-1.5 text-xs hover:bg-[#C9A96E]/10 transition-colors ${
                            i18n.language === l.code ? 'text-[#C9A96E] font-semibold' : 'text-[#1A1816]/70'
                          }`}>
                          {l.label}
                        </button>
                      ))}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              <button onClick={() => scrollToSection('#contact')} data-testid="header-cta-button" className="magnetic-btn">
                <span>{t('nav.contact')}</span>
              </button>
            </div>

            <button data-testid="mobile-menu-button" className="lg:hidden p-2 relative z-50"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}>
              {isMobileMenuOpen ? <X size={28} /> : <Menu size={28} />}
            </button>
          </nav>
        </div>
      </header>

      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div data-testid="mobile-menu"
            className="fixed inset-0 z-40 bg-[#FAF8F5] flex flex-col justify-center items-center"
            initial={{ clipPath: 'circle(0% at 95% 5%)' }} animate={{ clipPath: 'circle(150% at 95% 5%)' }}
            exit={{ clipPath: 'circle(0% at 95% 5%)' }} transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}>
            <nav className="flex flex-col items-center gap-6">
              {navLinks.map((link, i) => (
                <motion.button key={link.href} onClick={() => scrollToSection(link.href)}
                  className="text-2xl font-sans font-bold text-[#1A1816] hover:text-[#C9A96E] transition-colors"
                  initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 + i * 0.08 }}>
                  {link.label}
                </motion.button>
              ))}
              {/* Mobile lang switcher */}
              <div className="flex gap-3 mt-4">
                {langs.map((l) => (
                  <button key={l.code} onClick={() => changeLang(l.code)}
                    className={`text-xs font-semibold px-3 py-1.5 border transition-colors ${
                      i18n.language === l.code ? 'border-[#C9A96E] text-[#C9A96E]' : 'border-[#1A1816]/10 text-[#8A857D]'
                    }`}>{l.label}</button>
                ))}
              </div>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Header;
